import {cloneElement, useContext, useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {AddCircleOutline} from '@mui/icons-material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import EditIcon from '@mui/icons-material/Edit';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import PaidIcon from '@mui/icons-material/Paid';
import SpaIcon from '@mui/icons-material/Spa';
import {Box, Card, Grid, IconButton, SxProps, Typography, CardContent, CardHeader} from '@mui/material';

import {CompanyProfile, Benefit} from '../types';

import {CompanyProfileAddBenefitsForm} from './forms/CompanyProfileBenefitsAddForm';
import {CompanyProfileBenefitsForm} from './forms/CompanyProfileBenefitsForm';
import {BENEFIT_CHECK_COLOR, getBenefitCardHeaderSx, getBenefitCardSx} from './profileBenefitStyles';
import {PROFILE_DIALOG_SX, PROFILE_DIALOG_TITLE_SX} from './profileDialogStyles';
import {ProfileSection} from './ProfileSection';
import EmpionDialog from '../../../../components/EmpionDialog/EmpionDialog';
import {LanguageContext} from '../../../../contexts/LanguageContext/LanguageContext';
import {useToggle} from '../../../../hooks/useToggle';

type CompanyProfileBenefitsProps = {
  company: CompanyProfile;
  updateBenefits(data: CompanyProfile['benefits']): void;
  createBenefits(data: CompanyProfile['benefits']): void;
};

const benefitItemStyle: SxProps = {
  display: 'flex',
  marginBottom: '16px',
  overflowWrap: 'anywhere',
};

export const CompanyProfileBenefits = ({company, createBenefits, updateBenefits}: CompanyProfileBenefitsProps) => {
  const {t} = useTranslation();
  const currentLanguage = useContext(LanguageContext);

  const [isEditBenefitsModalOpen, toggleEditBenefitsModal] = useToggle(false);
  const [isAddBenefitsModalOpen, toggleAddBenefitsModal] = useToggle(false);

  const handleFormSubmit = (data: CompanyProfile) => {
    updateBenefits(data.benefits);
    toggleEditBenefitsModal();
  };

  const handleCreateFormSubmit = (data: CompanyProfile['benefits']) => {
    createBenefits(data);
    toggleAddBenefitsModal();
  };

  const handleFormCancel = () => {
    toggleEditBenefitsModal();
  };

  const categoryIcons: {[key: string]: JSX.Element} = {
    happiness: <EmojiEmotionsIcon />,
    office: <CorporateFareIcon />,
    health: <SpaIcon />,
    financial: <PaidIcon />,
  };

  const renderBenefits = (category: string) => {
    const filteredBenefits = company.benefits?.filter(
      (benefit) => benefit.category.categoryKey === category && benefit.selectedAt
    );

    if (!filteredBenefits || filteredBenefits.length === 0) {
      return null;
    }

    return filteredBenefits.map((benefit, key) => {
      // Check if the labels exist for the current language
      const label = benefit.labels?.[currentLanguage];
      if (!label) {
        return null;
      }
      return (
        <Grid key={key} item xs={12}>
          <Typography variant='body1' sx={benefitItemStyle}>
            <CheckCircleOutlineIcon sx={{marginRight: '8px', marginTop: '3px', color: BENEFIT_CHECK_COLOR}} />
            {label}
          </Typography>
        </Grid>
      );
    });
  };

  const selectedCategories = useMemo(() => {
    const categories: Record<string, Benefit['category']> = {};

    company.benefits
      ?.filter((benefit) => !!benefit.selectedAt)
      .map((benefit) => {
        categories[benefit.category.categoryKey] = benefit.category;
      });

    return Object.values(categories);
  }, [company]);

  const categoryCount = selectedCategories.length;
  const columnCount = categoryCount > 0 ? Math.floor(12 / categoryCount) : 12;

  return (
    <ProfileSection
      title={t('companyProfile.benefits.title')}
      dataTour='profile.benefits'
      actions={
        <>
          <IconButton onClick={() => toggleAddBenefitsModal(true)}>
            <AddCircleOutline />
          </IconButton>
          <IconButton onClick={() => toggleEditBenefitsModal()}>
            <EditIcon />
          </IconButton>
        </>
      }
    >
      <Box component='main' sx={{flexGrow: 1}}>
        {selectedCategories.length !== 0 ? (
          <Grid container item spacing={2}>
            {selectedCategories.map((category, index) => (
              <Grid key={index} item xs={12} sm={columnCount}>
                <Card sx={getBenefitCardSx()}>
                  <CardHeader
                    sx={getBenefitCardHeaderSx()}
                    title={
                      <Typography variant='h6' textTransform={'capitalize'} color={'white'}>
                        {category.labels?.[currentLanguage]}
                      </Typography>
                    }
                    avatar={cloneElement(categoryIcons[category.categoryKey], {
                      sx: {
                        fontSize: '32px',
                        color: 'white',
                      },
                    })}
                  />
                  <CardContent sx={{flex: 1}}>{renderBenefits(category.categoryKey)}</CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container direction='column' alignItems='center' sx={{opacity: '0.3'}}>
            <Typography mb={2}>{t('companyProfile.benefits.emptyState')}</Typography>
          </Grid>
        )}
      </Box>
      <EmpionDialog
        open={isAddBenefitsModalOpen}
        onClose={() => toggleAddBenefitsModal(false)}
        maxWidth='lg'
        showCloseButton
        title={t('companyProfile.benefits.dialog.create.title')}
        sxTitle={PROFILE_DIALOG_TITLE_SX}
        sx={PROFILE_DIALOG_SX}
      >
        <CompanyProfileAddBenefitsForm
          company={company}
          onCancel={() => toggleAddBenefitsModal(false)}
          onSubmit={handleCreateFormSubmit}
        />
      </EmpionDialog>
      <EmpionDialog
        open={isEditBenefitsModalOpen}
        onClose={() => toggleEditBenefitsModal()}
        maxWidth='lg'
        showCloseButton
        title={t('companyProfile.benefits.dialog.update.title')}
        sxTitle={PROFILE_DIALOG_TITLE_SX}
        sx={PROFILE_DIALOG_SX}
      >
        <CompanyProfileBenefitsForm company={company} onCancel={handleFormCancel} onSubmit={handleFormSubmit} />
      </EmpionDialog>
    </ProfileSection>
  );
};
